import { useEffect } from 'react';
import {
  ClipboardCheck, Home, Inbox, LayoutTemplate, Mail, MailPlus, Settings, type LucideIcon,
} from 'lucide-react';
import { useTutorialStore } from '../../stores/tutorialStore';
import type { TourRechte, TourRolle } from '../../lib/touren';
import './EinfuehrungsModal.css';

type Bereich = { Icon: LucideIcon; titel: string; text: string; rolle?: TourRolle };

/**
 * Die Bereiche, die im Begrüßungsfenster angekündigt werden. Die
 * Rollenangaben sind dieselben wie in der Tour: Sonst kündigt das Fenster
 * etwas an, das die Tour dann auslässt.
 */
const BEREICHE: Bereich[] = [
  { Icon: Home, titel: 'Start', text: 'Was bei Dir liegt und was bei der Redaktion wartet.', rolle: 'einreicher' },
  { Icon: Home, titel: 'Dashboard', text: 'Zahlen, letzte Kampagnen und was auf eine Freigabe wartet.', rolle: 'team' },
  { Icon: MailPlus, titel: 'Neue Mail', text: 'Der Assistent: Vorlage, Vorab-Fragen, Inhalt, Empfänger, Einreichen.', rolle: 'vorbereiten' },
  { Icon: Inbox, titel: 'Meine Mails', text: 'Wo Deine Mails stehen — und wo jemand auf Dich wartet.', rolle: 'vorbereiten' },
  { Icon: LayoutTemplate, titel: 'Vorlagen', text: 'Acht Vorlagen, gebaut aus den Mails der letzten Monate.' },
  { Icon: ClipboardCheck, titel: 'Freigaben', text: 'Was auf Deine Entscheidung wartet — und wer wann was entschieden hat.', rolle: 'redaktion' },
  { Icon: Mail, titel: 'Kampagnen & Gruppen', text: 'Der freie Editor, Empfängergruppen samt CRM-Tags und Kontakte.', rolle: 'team' },
  { Icon: Settings, titel: 'Einstellungen', text: 'Wer was darf, Freigaberecht und Absender.', rolle: 'admin' },
];

type Props = {
  offen: boolean;
  vorname: string;
  rechte: TourRechte;
  onTour: () => void;
  onSpaeter: () => void;
};

export function EinfuehrungsModal({ offen, vorname, rechte, onTour, onSpaeter }: Props) {
  const { an, umschalten } = useTutorialStore();

  useEffect(() => {
    if (!offen) return;
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') onSpaeter(); };
    document.addEventListener('keydown', taste);
    const vorher = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', taste);
      document.body.style.overflow = vorher;
    };
  }, [offen, onSpaeter]);

  if (!offen) return null;

  const sichtbar = BEREICHE.filter((b) => !b.rolle || rechte[b.rolle]);

  return (
    // Bewusst kein Schließen per Klick daneben: Das Fenster erscheint genau
    // einmal, und ein Fehlklick soll es nicht wegwischen.
    <div className="eintro-hinter" role="dialog" aria-modal="true" aria-labelledby="eintro-titel">
      <div className="eintro">
        <div className="eintro-kopf">
          <h2 className="eintro-titel" id="eintro-titel">
            {vorname ? `Willkommen, ${vorname}!` : 'Willkommen!'}
          </h2>
          <p className="eintro-unter">
            Randori Mail ist die Stelle für alle Mails an Mitglieder und Eltern der Randori-Pro
            Sportschulen — im selben Layout wie alle unsere Mails.
          </p>
        </div>

        <div className="eintro-inhalt">
          {rechte.einreicher ? (
            <p className="eintro-text">
              Hier bereitest Du Mails an Deine Sportschule selbst vor — ohne HTML. Du wählst eine
              Vorlage, beantwortest ein paar Fragen vorab, füllst die Felder aus und reichst die
              Mail ein. Die Redaktion prüft und gibt frei; dann geht sie zu Deinem Wunschtermin raus.
            </p>
          ) : (
            <p className="eintro-text">
              Neu: Geschäftsführung und Studioleitung bereiten eigene Mails im Assistenten vor und
              reichen sie ein. Die Redaktion prüft, fragt nach und gibt frei — erst dann entsteht
              der Brevo-Entwurf und die Mail geht raus.
            </p>
          )}

          <p className="eintro-label">Diese Bereiche stehen Dir offen</p>
          <div className="eintro-bereiche">
            {sichtbar.map((b) => (
              <div className="eintro-bereich" key={b.titel}>
                <span className="eintro-bereich-symbol" aria-hidden="true"><b.Icon size={16} /></span>
                <div>
                  <div className="eintro-bereich-titel">{b.titel}</div>
                  <div className="eintro-bereich-text">{b.text}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="eintro-tutorial">
            <div>
              <div className="eintro-tutorial-titel">Tutorial-Modus: Erklärungen auf jeder Seite</div>
              <div className="eintro-tutorial-text">
                Ein Kasten oben erklärt, wofür die Seite da ist und was zu tun ist. Jederzeit über
                den Doktorhut in der Kopfzeile umschaltbar.
              </div>
            </div>
            <div className="seg-control" role="group" aria-label="Tutorial-Modus">
              <button
                type="button"
                className={`seg-btn${an ? ' active' : ''}`}
                aria-pressed={an}
                onClick={() => { if (!an) void umschalten(); }}
              >
                An
              </button>
              <button
                type="button"
                className={`seg-btn${!an ? ' active' : ''}`}
                aria-pressed={!an}
                onClick={() => { if (an) void umschalten(); }}
              >
                Aus
              </button>
            </div>
          </div>
        </div>

        <div className="eintro-fuss">
          <button type="button" className="btn btn-ghost" onClick={onSpaeter}>
            Später
          </button>
          <button type="button" className="btn btn-primary" onClick={onTour}>
            Rundgang starten
          </button>
        </div>
      </div>
    </div>
  );
}
